import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faSave,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import { useAxiosInstance } from "@/utils/useAxiosInstance";

interface OperatingHour {
  id: number;
  day: string;
  open_time: string;
  close_time: string;
  branch: number;
}

interface BranchOperatingHoursInputProps {
  operatingHours: OperatingHour[];
  setOperatingHours: React.Dispatch<React.SetStateAction<OperatingHour[]>>;
  branchId: number;
}

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const getDayIndex = (day: string): number => {
  return daysOfWeek.indexOf(day);
};

const sortOperatingHours = (hours: OperatingHour[]): OperatingHour[] => {
  return [...hours].sort((a, b) => {
    const dayDiff = getDayIndex(a.day) - getDayIndex(b.day);
    if (dayDiff !== 0) return dayDiff;

    return a.open_time.localeCompare(b.open_time);
  });
};

// Helper function to format time by removing seconds
const formatTime = (timeString: string): string => {
  // If the time includes seconds (HH:MM:SS format), remove the seconds part
  if (timeString && timeString.split(':').length === 3) {
    return timeString.split(':').slice(0, 2).join(':');
  }
  return timeString;
};

const BranchOperatingHoursInput: React.FC<BranchOperatingHoursInputProps> = ({
  operatingHours,
  setOperatingHours,
  branchId,
}) => {
  const [axiosInstance] = useAxiosInstance();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [localHours, setLocalHours] = useState<OperatingHour[]>(operatingHours);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteHourId, setDeleteHourId] = useState<number | null>(null);

  const sortedLocalHours = useMemo(
    () => sortOperatingHours(localHours),
    [localHours],
  );

  useEffect(() => {
    setLocalHours(operatingHours);
  }, [operatingHours]);

  const handleAddOperatingHour = () => {
    const newHour: OperatingHour = {
      id: -Date.now(),
      day: "Wednesday",
      open_time: "11:00",
      close_time: "21:00",
      branch: branchId,
    };
    setLocalHours((prev) => [...prev, newHour]);
    setEditingId(newHour.id);
    setIsDirty(true);
  };

  const handleEditOperatingHour = (id: number) => {
    setEditingId(id);
  };

  const handleSaveOperatingHour = async (id: number) => {
    try {
      const hourToSave = localHours.find((hour) => hour.id === id);
      if (!hourToSave) return;

      const apiUrl = `${
        import.meta.env.VITE_APP_API_BASE_URL
      }api/v1/operating-hours/`;
      let savedHour: OperatingHour;

      if (id < 0) {
        // New hour
        const { id: _, ...hourData } = hourToSave;
        const response = await axiosInstance.post(apiUrl, hourData);
        savedHour = response.data;
      } else {
        // Existing hour
        const response = await axiosInstance.patch(
          `${apiUrl}${id}/`,
          hourToSave,
        );
        savedHour = response.data;
      }

      setLocalHours((prev) =>
        prev.map((hour) => (hour.id === id ? savedHour : hour)),
      );
      setOperatingHours((prev) =>
        prev.map((hour) => (hour.id === id ? savedHour : hour)),
      );
      setEditingId(null); // Reset editing mode
      setIsDirty(false);
    } catch (error) {
      console.error("Error saving operating hour:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setLocalHours(operatingHours);
    setIsDirty(false);
  };

  const handleInputChange = (id: number, field: string, value: string) => {
    setLocalHours((prev) =>
      prev.map((hour) => (hour.id === id ? { ...hour, [field]: value } : hour)),
    );
    setIsDirty(true);
  };

  const handleDeleteOperatingHour = (id: number) => {
    setDeleteHourId(id);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteOperatingHour = async () => {
    if (deleteHourId === null) return;

    try {
      if (deleteHourId >= 0) {
        const apiUrl = `${
          import.meta.env.VITE_APP_API_BASE_URL
        }api/v1/operating-hours/${deleteHourId}/`;
        await axiosInstance.delete(apiUrl);
      }

      // Update both local and parent state
      const updatedHours = localHours.filter(
        (hour) => hour.id !== deleteHourId,
      );
      setLocalHours(updatedHours);
      setIsDirty(true);

      setShowDeleteConfirmation(false);
      setDeleteHourId(null);
    } catch (error) {
      console.error("Error deleting operating hour:", error);
    }
  };

  const saveAllChanges = async () => {
    setIsSaving(true);
    try {
      const apiUrl = `${
        import.meta.env.VITE_APP_API_BASE_URL
      }api/v1/operating-hours/`;

      const updatedHours: OperatingHour[] = [];

      for (const hour of localHours) {
        if (hour.id < 0) {
          // New hour, create with POST
          const { id, ...hourData } = hour;
          const response = await axiosInstance.post(apiUrl, hourData);
          updatedHours.push(response.data);
        } else {
          // Existing hour, update with PATCH
          const response = await axiosInstance.patch(
            `${apiUrl}${hour.id}/`,
            hour,
          );
          updatedHours.push(response.data);
        }
      }

      setOperatingHours(updatedHours);
      setLocalHours(updatedHours);
      setIsDirty(false);
      setEditingId(null);
    } catch (error) {
      console.error("Error saving operating hours:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const discardChanges = () => {
    setLocalHours(operatingHours);
    setIsDirty(false);
    setEditingId(null);
  };

  const renderOperatingHour = (hour: OperatingHour) => {
    const isEditing = editingId === hour.id;

    return (
      <div key={hour.id} className="mb-4 overflow-hidden rounded-lg bg-gray-50">
        <div className="flex flex-col p-4 md:flex-row md:items-center md:space-x-4">
          <div className="mb-4 flex-grow text-2xl md:mb-0 md:grid md:grid-cols-3 md:gap-4 md:text-2xl">
            <div className="mb-2 font-medium md:mb-0">{hour.day}</div>
            <div className="flex justify-between md:col-span-2">
              <span>{formatTime(hour.open_time)}</span>
              <span className="mx-2"> - </span>
              <span>{formatTime(hour.close_time)}</span>
            </div>
          </div>
          <div className="flex items-center justify-center space-x-4 md:justify-end md:space-x-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={() => handleSaveOperatingHour(hour.id)}
                  className="flex-1 rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faSave} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 rounded bg-gray-500 p-2 text-white transition-colors hover:bg-gray-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleEditOperatingHour(hour.id)}
                  className="flex-1 rounded bg-gray-500 p-2 text-white transition-colors hover:bg-gray-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDeleteOperatingHour(hour.id)}
                  className="flex-1 rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:flex-none"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEditingHour = (hour: OperatingHour) => {
    return (
      <div key={hour.id} className="mb-4 overflow-hidden rounded-lg bg-gray-50">
        <div className="flex flex-col p-4 md:flex-row md:items-center md:space-x-4">
          <div className="mb-4 grid flex-grow grid-cols-1 gap-4 md:mb-0 md:grid-cols-3">
            <div>
              <select
                value={hour.day}
                onChange={(e) =>
                  handleInputChange(hour.id, "day", e.target.value)
                }
                className="text- w-full rounded border p-2"
              >
                {daysOfWeek.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-between space-x-2 md:col-span-2">
              <input
                type="time"
                value={formatTime(hour.open_time)}
                onChange={(e) =>
                  handleInputChange(hour.id, "open_time", e.target.value)
                }
                className="text-md flex-1 rounded border p-2"
              />
              <span className="mx-2 flex items-center"> - </span>
              <input
                type="time"
                value={formatTime(hour.close_time)}
                onChange={(e) =>
                  handleInputChange(hour.id, "close_time", e.target.value)
                }
                className="text-md flex-1 rounded border p-2"
              />
            </div>
          </div>
          <div className="flex items-center justify-center space-x-4 md:justify-end md:space-x-2">
            <button
              type="button"
              onClick={() => handleSaveOperatingHour(hour.id)}
              className="flex-1 rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:flex-none"
            >
              <FontAwesomeIcon icon={faSave} />
            </button>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="flex-1 rounded bg-gray-500 p-2 text-white transition-colors hover:bg-gray-600 md:flex-none"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow">
      {isDirty && (
        <ContextualSaveBar
          isDirty={isDirty}
          onSave={saveAllChanges}
          onDiscard={discardChanges}
          isSaving={isSaving}
        />
      )}
      <div className="p-4 md:mx-auto md:max-w-3xl md:p-6">
        <h3 className="mb-4 text-xl font-bold md:mb-6">Operating Hours</h3>
        <div className="space-y-2 md:space-y-4">
          {sortedLocalHours.map((hour) =>
            editingId === hour.id
              ? renderEditingHour(hour)
              : renderOperatingHour(hour),
          )}
        </div>
        <button
          type="button"
          onClick={handleAddOperatingHour}
          className="mt-4 w-full rounded bg-red-500 p-2 text-white transition-colors hover:bg-red-600 md:mt-6 md:w-auto"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add Operating Hour
        </button>
      </div>

      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-bold">Confirm Deletion</h2>
            <p className="mb-6">
              Are you sure you want to delete this operating hour?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(false)}
                className="rounded-md bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteOperatingHour}
                className="rounded-md bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchOperatingHoursInput;
